import type { AppProps } from 'next/app';
import '@/styles/globals.css';
import '../components/registration';
import '@/mocks';
import { ColorProvider } from '@/utils/ColorProvider';
import SentryErrorBoundary from '@/components/SentryErrorBoundary';
import posthog from '@/lib/posthogWrapper';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { PostHogProvider } from '@/lib/posthogProvider';
import { getSubdomain } from '@/utils/getDomain';
import { getTrackingParams } from '@/utils/analytics';
import { getJSONCookie } from '@/lib/cookies';
if (typeof window !== 'undefined' && !process.env.CI) {
  const urlParams = new URLSearchParams(window.location.search);

  // Read bootstrapped flags from cookies set by middleware (runs before React)
  const bootstrappedFlags = getJSONCookie<Record<string, string | boolean>>('ph_bootstrap_flags') || {};
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'always',
    bootstrap: Object.keys(bootstrappedFlags).length > 0 ? {
      featureFlags: bootstrappedFlags
    } : undefined,
    disable_session_recording: true,
    loaded: (ph: typeof posthog) => {
      ph.group('Domain', getSubdomain());
      ph.group('Project Name', process.env.NEXT_PUBLIC_PROJECT_NAME as string || 'Unknown / Development');
      ph.register({
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
        url: window.location.pathname,
        project_name: process.env.NEXT_PUBLIC_PROJECT_NAME as string || 'Unknown / Development',
        domain: getSubdomain()
      });
      if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'development') {
        ph.debug();
      }
      ph.setPersonProperties({
        ...getTrackingParams(),
        EFID: urlParams.get('efid') || '',
        OID: urlParams.get('oid') || '',
        AID: urlParams.get('aid') || '',
        ef_offer_id: urlParams.get('ef_offer_id') || '',
        ef_affiliate_id: urlParams.get('ef_affiliate_id') || ''
      });

      // Reload feature flags with full context (groups + properties)
      // so subsequent reads get accurate values instead of the
      // initial /flags bootstrap that was sent without context
      posthog.reloadFeatureFlags();
    }
  });
}
export default function App({
  Component,
  pageProps
}: AppProps) {
  const router = useRouter();
  useEffect(() => {
    if (!router.isReady) return;

    // Track initial page load
    posthog.capture('$pageview', {
      path: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Track route changes
    const handleRouteChange = (url: string) => {
      posthog.capture('$pageview', {
        path: url,
        referrer: document.referrer,
        search: router.query,
        title: document.title
      });
    };

    // Track when user starts to leave a page
    const handleRouteStart = () => {
      posthog.capture('$pageleave', {
        path: router.asPath
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeStart', handleRouteStart);
    };
  }, [router.isReady]);
  useEffect(() => {
    const handleLoad = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      // Get Web Vitals if available
      let cls = 0;
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'layout-shift') {
            cls += (entry as unknown as {
              value: number;
            }).value;
          }
        }
      });
      observer.observe({
        entryTypes: ['layout-shift']
      });
      const fid = performance.getEntriesByName('first-input')?.['0']?.duration;
      const lcp = performance.getEntriesByName('largest-contentful-paint')?.['0']?.startTime;
      posthog.capture('performance_metrics', {
        // Time from start to DOM being interactive
        timeToInteractive: navigation.domInteractive - navigation.fetchStart,
        // Time until everything is loaded
        totalLoadTime: navigation.loadEventEnd - navigation.fetchStart,
        // Time spent processing load event handlers
        loadEventTime: navigation.loadEventEnd - navigation.loadEventStart,
        // Time to first byte
        timeToFirstByte: navigation.responseStart - navigation.requestStart,
        // Additional Core Web Vitals
        cumulativeLayoutShift: cls,
        firstInputDelay: fid,
        largestContentfulPaint: lcp,
        // Additional useful metrics
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
        firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
      });
    };
    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  useEffect(() => {
    const handleBeforeUnload = () => {
      posthog.capture('site_exit', {
        path: router.asPath,
        exitTime: new Date().toISOString(),
        timeOnPage: performance.now(),
        // milliseconds since page load
        hasUnsavedChanges: false,
        // you can set this based on your app's state
        exitType: 'browser_close_or_navigation'
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [router.asPath]);
  return <SentryErrorBoundary data-sentry-element="SentryErrorBoundary" data-sentry-component="App" data-sentry-source-file="_app.tsx">
			<PostHogProvider client={posthog} data-sentry-element="PostHogProvider" data-sentry-source-file="_app.tsx">
				<ColorProvider data-sentry-element="ColorProvider" data-sentry-source-file="_app.tsx">
					<Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
					<div style={{
          display: 'none'
        }} id="hidden-form-data"></div>
				</ColorProvider>
			</PostHogProvider>
		</SentryErrorBoundary>;
}